import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Bell } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { Empty, Field, Loader, PageHeader } from "../components/common";
import { Switch } from "../components/ui/switch";
import { timeAgo } from "../components/NotificationBell";

const TYPES = [
  ["noticias", "Notícias do torneio", "Comunicados da organização"],
  ["etapas", "Etapas e chaves", "Novas etapas e sorteio das chaves"],
  ["feed", "Feed", "Publicações da organização"],
  ["cobrancas", "Cobranças", "Novas cobranças para você"],
  ["votacao", "Votações", "Novas votações abertas"],
  ["loja", "Loja", "Novidades da loja"],
];

const Broadcast = () => {
  const [f, setF] = useState({ title: "", body: "", link: "" });
  const send = async (e) => {
    e.preventDefault();
    try { await api.post("/notifications/broadcast", f); toast.success("Notícia enviada a todos!"); setF({ title: "", body: "", link: "" }); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={send} className="card-x space-y-3 p-5" data-testid="broadcast-form">
      <div className="h-display text-2xl">Enviar notícia a todos</div>
      <Field label="Título"><input required data-testid="broadcast-title-input" className="inp" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Inscrições da 2ª etapa abertas!" /></Field>
      <Field label="Mensagem"><textarea rows={3} data-testid="broadcast-body-input" className="inp" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /></Field>
      <Field label="Abrir página ao tocar (opcional)">
        <select data-testid="broadcast-link-select" className="inp" value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })}>
          <option value="">Nenhuma</option><option value="/app">Início</option><option value="/app/etapas">Etapas</option><option value="/app/ranking">Ranking</option>
          <option value="/app/loja">Loja</option><option value="/app/votacao">Votação</option><option value="/app/brindes">Brindes</option>
        </select>
      </Field>
      <button data-testid="broadcast-send-btn" className="btn-lime flex w-full items-center justify-center gap-2 rounded-full py-3"><Send className="h-4 w-4" />Enviar</button>
    </form>
  );
};

export default function Notifications() {
  const { user, isAdmin, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [prefs, setPrefs] = useState(user.notif_prefs || {});
  const load = () => api.get("/notifications").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (k, v) => {
    setPrefs({ ...prefs, [k]: v });
    try { const { data: p } = await api.put("/me/notif-prefs", { [k]: v }); setPrefs(p); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const types = isAdmin ? [...TYPES, ["admin", "Administração", "Novos logins aguardando conexão/aprovação"]] : TYPES;
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Avisos" title="Notificações" action={data?.unread > 0 && <button data-testid="page-read-all" onClick={async () => { await api.post("/notifications/read-all"); load(); }} className="btn-ghost">Marcar todas como lidas</button>} />
      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="space-y-2">
          {!data ? <Loader /> : data.items.length === 0 ? <Empty icon={Bell} title="Tudo em dia" text="Você não tem notificações." /> : data.items.map((n) => (
            <button key={n.id} data-testid={`notif-row-${n.id}`} onClick={async () => { if (!n.read) await api.post(`/notifications/${n.id}/read`); if (n.link) navigate(n.link); else load(); }}
              className={`card-x flex w-full gap-3 p-4 text-left ${n.read ? "" : "border-[#E4F222]/50"}`}>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-[#232B3E]" : "bg-[#E4F222]"}`} />
              <span className="flex-1"><span className="block font-semibold">{n.title}</span>{n.body && <span className="block text-sm text-[#94A3B8]">{n.body}</span>}<span className="text-[11px] text-[#64748B]">{timeAgo(n.created_at)}</span></span>
            </button>
          ))}
        </div>
        <div className="space-y-6">
          <div className="card-x p-5">
            <div className="h-display mb-3 text-2xl">O que quero receber</div>
            {types.map(([k, l, d]) => (
              <div key={k} className="flex items-center justify-between gap-3 border-b border-[#232B3E] py-3 last:border-0">
                <div><div className="text-sm font-semibold">{l}</div><div className="text-xs text-[#64748B]">{d}</div></div>
                <Switch data-testid={`pref-${k}`} checked={prefs[k] !== false} onCheckedChange={(v) => toggle(k, v)} />
              </div>
            ))}
          </div>
          {isAdmin && <Broadcast />}
        </div>
      </div>
    </div>
  );
}
